import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchAccountsByAccountingIds } from "@/api/fetchAccountsByAccountingIds/fetchAccountsByAccountingIdsApi";

const STOCHASTIC_CORPORATION = "Stochastic";

interface AccountBillabilityState {
  billability: Partial<Record<string, boolean>>;
  loading: boolean;
  error: string | null;
}

const initialState: AccountBillabilityState = {
  billability: {},
  loading: false,
  error: null,
};

const accountBillabilitySlice = createSlice({
  name: "accountBillability",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setBillability: (
      state,
      action: PayloadAction<Partial<Record<string, boolean>>>,
    ) => {
      Object.assign(state.billability, action.payload);
    },
  },
});

export const { setLoading, setError, setBillability } =
  accountBillabilitySlice.actions;

export const fetchAccountBillabilityAction =
  (accountingIds: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (accountingIds.length === 0) return;

    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await fetchAccountsByAccountingIds(STOCHASTIC_CORPORATION.toLowerCase(), accountingIds);
      const billability: Partial<Record<string, boolean>> = {};

      for (const [id, account] of Object.entries(response.data)) {
        billability[id] =
          account !== null &&
          account.preferredPaySources?.[STOCHASTIC_CORPORATION] != null;
      }

      dispatch(setBillability(billability));
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch account billability",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default accountBillabilitySlice.reducer;
